"use client";
import React, { forwardRef } from "react";
import { cn } from "../../lib/utilsComprehensive";
import { useA11yId } from "../../utils/a11y";
import {
  ContrastGuard,
  TextWithContrast,
} from "@/components/accessibility/ContrastGuard";
import { ANIMATION } from "../../tokens/designConstants";
import { useReducedMotion } from "../../hooks/useReducedMotion";

export interface GlassBadgeLineProps extends React.HTMLAttributes<HTMLDivElement> {
  items: {
    label: string;
    intent?: "default" | "success" | "warning" | "danger";
  }[];
  className?: string;
  /** ARIA label for the badge line */
  "aria-label"?: string;
}

export const GlassBadgeLine = forwardRef<HTMLDivElement, GlassBadgeLineProps>(
  (
    {
      // ContrastGuard text coverage is tracked in the manual accessibility QA report.

      items: incomingItems = [],
      "aria-label": ariaLabel,
      className,
      ...props
    },
    ref
  ) => {
    const badgeLineId = useA11yId("badge-line");
    const items = Array.isArray(incomingItems) ? incomingItems : [];

    return (
      <div
        data-glass-component
        ref={ref}
        id={badgeLineId}
        className={cn("glass-flex glass-flex-wrap glass-gap-1", className)}
        role="list"
        aria-label={ariaLabel || "Badge list"}
        {...props}
      >
        {items.map((it, i) => (
          <span
            key={i}
            className={cn(
              "glass-px-2 glass-py-1 glass-text-xs glass-radius-full glass-border",
              it.intent === "success"
                ? "bg-emerald-500/15 text-emerald-300 glass-border-emerald-400/30"
                : it.intent === "warning"
                  ? "bg-amber-500/15 text-amber-300 glass-border-amber-400/30"
                  : it.intent === "danger"
                    ? "bg-red-500/15 text-red-300 glass-border-red-400/30"
                    : "bg-white/10 glass-text-primary/80 glass-border-white/20"
            )}
            role="listitem"
            aria-label={`${it.intent || "default"} badge: ${it.label}`}
          >
            {it.label}
          </span>
        ))}
      </div>
    );
  }
);

GlassBadgeLine.displayName = "GlassBadgeLine";

export default GlassBadgeLine;
